import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';
import Button3D from '../pages/Button3D';
import ProviderBtn from '../pages/ProviderBtn';
import { FaGoogle, FaMicrosoft, FaApple } from 'react-icons/fa';

const PROVIDER_META = {
  google: {
    label: 'Google',
    fullLabel: 'Google Calendar',
    icon: <FaGoogle color="#DB4437" />,
    gradientDefault: 'linear-gradient(145deg, #4285f4, #2563eb)',
    shadow: '0 4px 12px rgba(66,133,244,0.35), inset 0 1px 0 rgba(255,255,255,0.2)',
  },
  outlook: {
    label: 'Outlook',
    fullLabel: 'Outlook Calendar',
    icon: <FaMicrosoft color="#0078D4" />,
    gradientDefault: 'linear-gradient(145deg, #0078d4, #005a9e)',
    shadow: '0 4px 12px rgba(0,120,212,0.35), inset 0 1px 0 rgba(255,255,255,0.2)',
  },
  icloud: {
    label: 'iCloud',
    fullLabel: 'iCloud Calendar',
    icon: <FaApple color="#000" />,
    gradientDefault: 'linear-gradient(145deg, #1c1c1e, #3a3a3c)',
    shadow: '0 4px 12px rgba(28,28,30,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
  },
};

const CalendarConnectModal = ({ onClose, onSuccess }) => {
  const { user } = useAuth();
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectDone, setConnectDone] = useState(false);
  const [error, setError] = useState('');
  const [appleId, setAppleId] = useState('');
  const [appPassword, setAppPassword] = useState('');

  const authDoneRef = useRef(false);
  const cleanupRef = useRef(null);

  const connectOAuth = async (provider) => {
    setIsConnecting(true);
    setError('');
    authDoneRef.current = false;

    try {
      const res = await apiClient.post('/auth/oauth-url', { provider, email: user?.email });
      if (!res.data?.auth_url) {
        setIsConnecting(false);
        setError('Failed to get authorization URL. Please try again.');
        return;
      }

      const w = 600, h = 700;
      const left = Math.max(0, window.screen.width / 2 - w / 2);
      const top  = Math.max(0, window.screen.height / 2 - h / 2);
      const popup = window.open(
        res.data.auth_url,
        'sc_cal_oauth',
        `width=${w},height=${h},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
      );

      if (!popup) {
        setIsConnecting(false);
        setError('Popup was blocked. Please allow popups for this site and try again.');
        return;
      }

      let messageHandler;
      let onParentFocus;

      const cleanup = () => {
        window.removeEventListener('message', messageHandler);
        window.removeEventListener('focus', onParentFocus);
      };
      cleanupRef.current = cleanup;

      const handleCancelled = () => {
        cleanup();
        if (!authDoneRef.current) {
          setIsConnecting(false);
          setError('Authorization window was closed before completing. Please try again.');
        }
      };

      messageHandler = (event) => {
        if (event.data?.type !== 'CALENDAR_OAUTH_SUCCESS') return;
        authDoneRef.current = true;
        cleanup();
        // popup closes itself via window.close() in auth_receive.py — no need to call it here
        setIsConnecting(false);
        setConnectDone(true);
        setTimeout(() => onSuccess(), 800);
      };
      window.addEventListener('message', messageHandler);

      // Cancellation detection: when parent regains focus the popup was closed.
      // 500 ms delay lets any in-flight postMessage success land before we report cancellation.
      // Never reads popup.closed — avoids the Cross-Origin-Opener-Policy console warning.
      onParentFocus = () => {
        if (authDoneRef.current) return;
        setTimeout(() => {
          if (!authDoneRef.current) handleCancelled();
        }, 500);
      };
      window.addEventListener('focus', onParentFocus);
    } catch (e) {
      console.error('OAuth connect error:', e);
      setIsConnecting(false);
      setError(`Error: ${e.message}`);
    }
  };

  const connectICloud = async () => {
    if (!appleId.trim() || !appPassword.trim()) {
      setError('Please fill in your Apple ID and App-Specific Password.');
      return;
    }
    setIsConnecting(true);
    setError('');
    try {
      const res = await apiClient.post('/auth/icloud', {
        email: user.email,
        apple_id: appleId.trim(),
        app_password: appPassword.trim(),
      });
      if (res.data.connected) {
        setIsConnecting(false);
        setConnectDone(true);
        setTimeout(() => onSuccess(), 800);
      } else {
        setIsConnecting(false);
        setError(res.data.message || 'Invalid iCloud credentials. Please check and try again.');
      }
    } catch (e) {
      console.error('iCloud connect error:', e);
      setIsConnecting(false);
      setError(`Error: ${e.message}`);
    }
  };

  const handleAuthorize = () => {
    if (selectedProvider === 'icloud') {
      connectICloud();
    } else if (selectedProvider) {
      connectOAuth(selectedProvider);
    }
  };

  const meta = selectedProvider ? PROVIDER_META[selectedProvider] : null;
  const isICloud = selectedProvider === 'icloud';

  const btnLabel = connectDone
    ? `✓ ${meta?.label} Calendar Connected`
    : isConnecting
    ? (isICloud ? 'Connecting…' : `Opening ${meta?.label} Sign-In…`)
    : isICloud
    ? 'Connect iCloud Calendar'
    : `Authorize ${meta?.label} Calendar`;

  const btnGradient = connectDone
    ? 'linear-gradient(145deg, #10b981, #059669)'
    : isConnecting
    ? 'linear-gradient(145deg, #f59e0b, #d97706)'
    : (meta?.gradientDefault ?? 'linear-gradient(145deg, #4285f4, #2563eb)');

  const btnShadow = connectDone
    ? '0 4px 12px rgba(16,185,129,0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
    : isConnecting
    ? '0 4px 12px rgba(245,158,11,0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
    : (meta?.shadow ?? '');

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: '20px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) { cleanupRef.current?.(); onClose(); } }}
    >
      <div style={{
        background: '#fff', borderRadius: '16px', padding: '28px 32px',
        maxWidth: '640px', width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
      }}>
        {/* Header */}
        <div style={{ position: 'relative', textAlign: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111' }}>
            Connect Your Calendar
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#6b7280', lineHeight: 1.5 }}>
            SmartClose will sync transaction deadlines and tasks directly to your calendar.
          </p>
          <button
            onClick={() => { cleanupRef.current?.(); onClose(); }}
            style={{ position: 'absolute', top: 0, right: 0, background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#9ca3af', lineHeight: 1, padding: 0 }}
          >✕</button>
        </div>

        {/* Step 1: Provider selection */}
        <div style={{ marginBottom: '20px' }}>
          <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 600, color: '#374151', textAlign: 'center' }}>
            Choose your calendar provider:
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', justifyContent: 'center' }}>
            {Object.entries(PROVIDER_META).map(([key, pm]) => (
              <ProviderBtn
                key={key}
                icon={pm.icon}
                label={pm.label}
                selected={selectedProvider === key}
                onClick={() => {
                  if (connectDone || isConnecting) return;
                  setSelectedProvider(key);
                  setError('');
                }}
              />
            ))}
          </div>
        </div>

        {/* Step 2: iCloud credentials */}
        {selectedProvider === 'icloud' && !connectDone && (
          <div style={{
            background: '#f9fafb', border: '1px solid #e5e7eb',
            borderRadius: '8px', padding: '14px 16px', marginBottom: '16px',
          }}>
            <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#495057', lineHeight: 1.4 }}>
              <strong>iCloud Setup:</strong> Visit{' '}
              <a href="https://appleid.apple.com/account/manage" target="_blank" rel="noopener noreferrer"
                style={{ color: '#007bff' }}>appleid.apple.com</a>{' '}
              → Security → App-Specific Passwords → Generate a password labeled "SmartClose".
            </p>
            <div style={{ marginBottom: '8px' }}>
              <input
                type="email"
                placeholder="Apple ID (e.g., your.email@icloud.com)"
                value={appleId}
                onChange={(e) => { setAppleId(e.target.value); setError(''); }}
                style={{
                  width: '100%', padding: '6px 10px', fontSize: '13px',
                  border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <input
                type="password"
                placeholder="App-Specific Password (16 characters)"
                value={appPassword}
                onChange={(e) => { setAppPassword(e.target.value); setError(''); }}
                style={{
                  width: '100%', padding: '6px 10px', fontSize: '13px',
                  border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box',
                }}
              />
            </div>
          </div>
        )}


        {/* Authorize / Connect button */}
        {selectedProvider && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
            <Button3D
              onClick={handleAuthorize}
              disabled={isConnecting || connectDone}
              className="enhanced-button"
              style={{
                minWidth: '200px',
                alignSelf: 'center',
                fontSize: '13px',
                padding: '8px 20px',
                fontWeight: '600',
                background: btnGradient,
                boxShadow: btnShadow,
                color: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                opacity: (isConnecting || connectDone) ? 0.85 : 1,
                cursor: (isConnecting || connectDone) ? 'not-allowed' : 'pointer',
                transform: 'translateZ(0)',
                transition: 'all 0.2s ease',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
              }}
            >
              {btnLabel}
            </Button3D>

            {/* Back link */}
            {!isConnecting && !connectDone && (
              <button
                onClick={() => { setSelectedProvider(null); setError(''); setAppleId(''); setAppPassword(''); }}
                style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '12px', alignSelf: 'center', padding: 0 }}
              >
                ← Choose a different provider
              </button>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            marginTop: '14px', padding: '10px 14px', borderRadius: '8px',
            background: '#fef2f2', border: '1px solid #fca5a5',
            color: '#b91c1c', fontSize: '13px', lineHeight: 1.5,
          }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarConnectModal;
